import { Link, TrackingData } from '@ecm/router';

import { Bundle } from '../../../api/dashboard/item/itemSection/consoFamily/bundle';
import { BundleCodeEnum } from '../../../api/dashboard/item/itemSection/consoFamily/bundleCodeEnum';
import { BundleSummary } from '../../../api/dashboard/item/itemSection/consoFamily/bundleSummary';
import { ConsoFamilyEnum } from '../../../api/dashboard/item/itemSection/consoFamily/consoFamilyEnum';
import { Equipment } from '../../../api/dashboard/item/itemSection/equipment';
import { ItemSection } from '../../../api/dashboard/item/itemSection/itemSection';
import { UsageCodeEnum } from '../../../api/dashboard/item/itemSection/usageCodeEnum';
import { PushSubItemViewModel } from '../../../model/home/pushSubItem.view-model';
import { SubItemViewModel } from '../../../model/home/subItem.view-model';
import { SubItemTypeEnum } from '../../../model/home/subItemTypeEnum';
import { TmsCodes } from '../../../model/tracking/tmsCodes.enum';
import { TrackedName } from '../../../model/tracking/trackedName.enum';
import { TrackedPage } from '../../../model/tracking/trackedPage.enum';
import { DashboardV7MapperUtils } from '../dashboard/dashboardV7-utils.mapper';

export class DeviceSimMapper {
    /** 🔧 Helper: Initialize SubItemViewModel with common properties */
    private static createBasePictoSubItem(
        cid: string,
        label: string,
        picto: string,
        bundleCode?: string,
    ): SubItemViewModel {
        const subItem: SubItemViewModel = new SubItemViewModel();
        subItem.detail = [];
        subItem.detailLong = [];
        subItem.cid = cid;
        subItem.type = SubItemTypeEnum.picto;
        subItem.label = label;
        subItem.picto = picto;
        subItem.bundleCode = bundleCode;
        return subItem;
    }

    /** 🔧 Helper: Initialize tracking data for consumption items */
    private static createConsoTrackingData(trackingName = 'suivi_conso'): TrackingData {
        return Object.assign(new TrackingData(), {
            tmsTracking: {
                trackedPage: TrackedPage.dashboard,
                trackingArea: TmsCodes.care_hp_mobile,
                trackingName,
            },
        });
    }

    /** 🔧 Helper: Add tracking and link to consumption subitem */
    private static addConsoTracking(
        subItem: SubItemViewModel,
        link: Link,
        trackingName = 'suivi_conso',
    ): SubItemViewModel {
        subItem.link = link;
        subItem.trackingData = this.createConsoTrackingData(trackingName);
        return subItem;
    }

    private static identifyFreezeFlags(
        consoFamilies: any[],
        dashboardTranslations: any,
    ): { showNoExtraBalance: boolean; freeBiesLabel: string } {
        let showNoExtraBalance = false;
        let freeBies = false;
        let freeBiesLabel: string;

        for (const consoFamily of consoFamilies) {
            if (
                !showNoExtraBalance &&
                Object.hasOwn(consoFamily, 'bundle') &&
                consoFamily.bundle.bundleCode === BundleCodeEnum.horsForfait &&
                !consoFamily.bundle.summary.displayAlertIcon
            ) {
                showNoExtraBalance = true;
            }
            if (
                !freeBies &&
                Object.hasOwn(consoFamily, 'bundle') &&
                consoFamily.familyCode === ConsoFamilyEnum.credit_2 &&
                (consoFamily.bundle.bundleCode === BundleCodeEnum.EPUISE ||
                    consoFamily.bundle.bundleCode === BundleCodeEnum.SOLDE) &&
                consoFamily.bundle.summary.usageText
            ) {
                freeBies = true;
                freeBiesLabel =
                    consoFamily.bundle.bundleCode === BundleCodeEnum.EPUISE
                        ? dashboardTranslations['deviceSIM']['internetMobile']['exhaustedFreeBies']
                        : dashboardTranslations['deviceSIM']['internetMobile']['freeBies'].concat(
                              DashboardV7MapperUtils.convertMainCredit(
                                  consoFamily.bundle.summary.mainCredit.value,
                                  consoFamily.bundle.summary.mainCredit.unit,
                              ),
                          );
            }
        }

        return { showNoExtraBalance, freeBiesLabel };
    }

    private static buildErrorFamilySubItem(
        consoFamily: any,
        cid: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.createBasePictoSubItem(
            cid,
            dashboardTranslations['deviceSIM']['internetMobile']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            consoFamily?.bundle?.bundleCode,
        );
        subItem.consumption = dashboardTranslations['deviceSIM']['error']['consumption'];
        const link = DashboardV7MapperUtils.getConsoPath(
            cid,
            itemSection.idSuiviConso,
            dashboardTranslations,
            itemSection.equipmentId,
        );
        return this.addConsoTracking(subItem, link);
    }

    private static buildCreditFamilySubItem(
        consoFamily: any,
        cid: string,
        itemSection: ItemSection,
        freeBiesLabel: string,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.createBasePictoSubItem(
            cid,
            dashboardTranslations['deviceSIM']['internetMobile']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            consoFamily?.bundle?.bundleCode,
        );
        const link = DashboardV7MapperUtils.getConsoPath(
            cid,
            itemSection.idSuiviConso,
            dashboardTranslations,
            itemSection.equipmentId,
        );
        subItem.link = link;
        subItem.trackingData = this.createConsoTrackingData();

        const summary: BundleSummary = consoFamily.bundle.summary;

        if (summary.mainCredit) {
            subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile']['prePaidRestText'].concat(
                DashboardV7MapperUtils.convertMainCredit(summary.mainCredit.value, summary.mainCredit.unit) + ' €',
            );
        } else {
            subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile']['prePaidExhaustedText'];
            subItem.alert = true;
        }

        if (freeBiesLabel) {
            subItem.freeBies = freeBiesLabel;
        }

        return subItem;
    }

    private static buildCompteRechargeableSubItem(
        consoFamily: any,
        cid: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.createBasePictoSubItem(
            cid,
            dashboardTranslations['deviceSIM']['internetMobile']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            consoFamily?.bundle?.bundleCode,
        );
        const link = DashboardV7MapperUtils.getConsoPath(
            cid,
            itemSection.idSuiviConso,
            dashboardTranslations,
            itemSection.equipmentId,
        );
        subItem.link = link;
        subItem.trackingData = this.createConsoTrackingData();

        const summary: BundleSummary = consoFamily.bundle.summary;

        if (summary.mainCredit) {
            subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile']['topUp'].concat(
                dashboardTranslations['deviceSIM']['internetMobile']['restText'].concat(
                    DashboardV7MapperUtils.convertMainCredit(summary.mainCredit.value, summary.mainCredit.unit) + ' €',
                ),
            );
        } else {
            subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile']['exhaustedText'];
            subItem.alert = true;
        }

        return subItem;
    }

    private static buildGaugeSubItem(
        bundle: Bundle,
        cid: string,
        itemSection: ItemSection,
        showNoExtraBalance: boolean,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.createBasePictoSubItem(
            cid,
            dashboardTranslations['deviceSIM']['internetMobile']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            bundle?.bundleCode,
        );
        subItem.type = SubItemTypeEnum.gauge;
        subItem.gaugePercent = bundle.gaugeInfo.percent;
        subItem.totalData = bundle.gaugeInfo.to;
        subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile']['restText'].concat(
            bundle.summary.mainCredit
                ? DashboardV7MapperUtils.convertMainCredit(
                      bundle.summary.mainCredit.value,
                      bundle.summary.mainCredit.unit,
                  )
                : '',
        );
        const link = DashboardV7MapperUtils.getConsoPath(
            cid,
            itemSection.idSuiviConso,
            dashboardTranslations,
            itemSection.equipmentId,
        );
        subItem.link = link;
        subItem.trackingData = this.createConsoTrackingData();

        if (showNoExtraBalance) {
            subItem.noExtraBalance = dashboardTranslations['deviceSIM']['extraBalance']['restTextNull'];
        }

        return subItem;
    }

    private static buildExtraBalanceSubItem(
        bundle: Bundle,
        cid: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel | null {
        const summary: BundleSummary = bundle.summary;

        if (!summary.displayAlertIcon) {
            return null;
        }

        const subItem = this.createBasePictoSubItem(
            cid,
            dashboardTranslations['deviceSIM']['extraBalance']['restText'].concat(
                summary.mainCredit
                    ? DashboardV7MapperUtils.convertMainCredit(summary.mainCredit.value, summary.mainCredit.unit) + ' €'
                    : '',
            ),
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            bundle?.bundleCode,
        );
        subItem.alert = true;
        subItem.detail.push(dashboardTranslations['deviceSIM']['extraBalance']['consumption']);
        subItem.detailLong.push(dashboardTranslations['deviceSIM']['extraBalance']['consumption']);
        subItem.trackingData = this.createConsoTrackingData('hors_forfait');

        return subItem;
    }

    private static buildExhaustedSubItem(
        bundle: Bundle,
        consoFamily: any,
        cid: string,
        itemSection: ItemSection,
        showNoExtraBalance: boolean,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.createBasePictoSubItem(
            cid,
            dashboardTranslations['deviceSIM']['internetMobile']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            bundle?.bundleCode,
        );
        subItem.alert = true;
        const link = DashboardV7MapperUtils.getConsoPath(
            cid,
            itemSection.idSuiviConso,
            dashboardTranslations,
            itemSection.equipmentId,
        );
        subItem.link = link;
        subItem.trackingData = this.createConsoTrackingData();

        if (consoFamily.familyCode === ConsoFamilyEnum.internetMobile) {
            let consumption: string;
            switch (bundle.bundleCode) {
                case BundleCodeEnum.LIBI:
                    consumption = 'internetUnlimitedText';
                    subItem.alert = false;
                    break;
                case BundleCodeEnum.SeuilAtteint:
                    consumption = 'internetReducedText';
                    break;
                default:
                    consumption = 'internetExhaustedText';
                    break;
            }
            subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile'][consumption];
        } else {
            subItem.consumption = dashboardTranslations['deviceSIM']['internetMobile']['exhaustedText'];
        }

        if (showNoExtraBalance) {
            subItem.noExtraBalance = dashboardTranslations['deviceSIM']['extraBalance']['restTextNull'];
        }

        return subItem;
    }

    private static buildInternetMobileSubItem(
        consoFamily: any,
        cid: string,
        itemSection: ItemSection,
        showNoExtraBalance: boolean,
        dashboardTranslations: any,
    ): SubItemViewModel | SubItemViewModel[] {
        const bundle: Bundle = consoFamily.bundle;
        const summary: BundleSummary = bundle.summary;
        const subItems: SubItemViewModel[] = [];

        // Handle gauge display
        if (Object.hasOwn(bundle, 'gaugeInfo') && summary.displayGauge) {
            subItems.push(this.buildGaugeSubItem(bundle, cid, itemSection, showNoExtraBalance, dashboardTranslations));
        }

        // Handle extra balance (horsForfait)
        if (bundle.bundleCode === BundleCodeEnum.horsForfait) {
            const extraBalanceSubItem = this.buildExtraBalanceSubItem(bundle, cid, itemSection, dashboardTranslations);
            if (extraBalanceSubItem) {
                subItems.push(extraBalanceSubItem);
            }
        } else if (!summary.displayGauge) {
            // Handle exhausted/reduced states
            subItems.push(
                this.buildExhaustedSubItem(
                    bundle,
                    consoFamily,
                    cid,
                    itemSection,
                    showNoExtraBalance,
                    dashboardTranslations,
                ),
            );
        }

        return subItems.length === 1 ? subItems[0] : subItems;
    }

    private static buildServiceAndRecoSubItems(
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel[] {
        const subItems: SubItemViewModel[] = [];

        if (Object.hasOwn(itemSection, 'equipments')) {
            for (const equipment of itemSection.equipments) {
                subItems.push(this.getEquipment(equipment, dashboardTranslations));
            }
        }

        if (Object.hasOwn(itemSection, 'service24')) {
            subItems.push(this.getService24(itemSection, dashboardTranslations));
        }

        if (Object.hasOwn(itemSection, 'recoOffer')) {
            subItems.push(
                DashboardV7MapperUtils.extractZid(
                    'recoMobileOffer',
                    DashboardV7MapperUtils.getCommercialInfo(itemSection.recoOffer),
                ),
            );
        }

        if (Object.hasOwn(itemSection, 'recoMobileRenewal')) {
            subItems.push(
                DashboardV7MapperUtils.extractZid(
                    'recoMobileRenewal',
                    DashboardV7MapperUtils.getCommercialInfo(itemSection.recoMobileRenewal),
                ),
            );
        }

        if (Object.hasOwn(itemSection, 'reco')) {
            const pushSubItem: PushSubItemViewModel = DashboardV7MapperUtils.getCommercialInfo(itemSection.reco, true);
            pushSubItem.label = itemSection.reco.content;
            pushSubItem.detail = null;
            subItems.push(DashboardV7MapperUtils.extractZid('recoMobile', pushSubItem));
        }

        return subItems;
    }

    private static reorderExtraBalanceToEnd(subItemsViewModel: SubItemViewModel[]): SubItemViewModel[] {
        if (subItemsViewModel.length <= 1) {
            return subItemsViewModel;
        }

        const index = subItemsViewModel.findIndex(
            (subItem) => subItem.bundleCode === BundleCodeEnum.horsForfait && subItem.type === 'picto',
        );

        if (index === -1) {
            return subItemsViewModel;
        }

        const extraBalance = subItemsViewModel[index].label;

        subItemsViewModel.forEach((item) => {
            if (item.type !== 'picto') {
                item.noExtraBalance = extraBalance;
            }
        });

        subItemsViewModel.splice(index, 1);
        return subItemsViewModel;
    }

    private static buildSubItemForFamily(
        consoFamily: any,
        cid: string,
        itemSection: ItemSection,
        showNoExtraBalance: boolean,
        freeBiesLabel: string,
        dashboardTranslations: any,
    ): SubItemViewModel | SubItemViewModel[] | null {
        if (!Object.hasOwn(consoFamily, 'bundle')) {
            return null;
        }

        switch (consoFamily.familyCode) {
            case ConsoFamilyEnum.error:
                return this.buildErrorFamilySubItem(consoFamily, cid, itemSection, dashboardTranslations);
            case ConsoFamilyEnum.credit:
                return this.buildCreditFamilySubItem(
                    consoFamily,
                    cid,
                    itemSection,
                    freeBiesLabel,
                    dashboardTranslations,
                );
            case ConsoFamilyEnum.compteRechargeable:
                return this.buildCompteRechargeableSubItem(consoFamily, cid, itemSection, dashboardTranslations);
            default:
                return this.buildInternetMobileSubItem(
                    consoFamily,
                    cid,
                    itemSection,
                    showNoExtraBalance,
                    dashboardTranslations,
                );
        }
    }

    private static buildConsoFamiliesSubItems(
        consoFamilies: any[],
        cid: string,
        itemSection: ItemSection,
        showNoExtraBalance: boolean,
        freeBiesLabel: string,
        dashboardTranslations: any,
    ): SubItemViewModel[] {
        const subItems: SubItemViewModel[] = [];

        for (const consoFamily of consoFamilies) {
            if (consoFamily.familyCode === ConsoFamilyEnum.credit_2) {
                continue;
            }

            const subItem = this.buildSubItemForFamily(
                consoFamily,
                cid,
                itemSection,
                showNoExtraBalance,
                freeBiesLabel,
                dashboardTranslations,
            );

            if (subItem) {
                if (Array.isArray(subItem)) {
                    subItems.push(...subItem);
                } else {
                    subItems.push(subItem);
                }
            }
        }

        return subItems;
    }

    public static extractSubItemSim(
        itemSection: ItemSection,
        cid: string,
        dashboardTranslations: any,
    ): SubItemViewModel[] | null {
        if (!Object.hasOwn(itemSection, 'consoFamilies')) {
            return this.buildServiceAndRecoSubItems(itemSection, dashboardTranslations);
        }

        const { showNoExtraBalance, freeBiesLabel } = this.identifyFreezeFlags(
            itemSection.consoFamilies,
            dashboardTranslations,
        );

        const subItemsViewModel: SubItemViewModel[] = [];
        subItemsViewModel.push(
            ...this.buildConsoFamiliesSubItems(
                itemSection.consoFamilies,
                cid,
                itemSection,
                showNoExtraBalance,
                freeBiesLabel,
                dashboardTranslations,
            ),
        );

        subItemsViewModel.push(...this.buildServiceAndRecoSubItems(itemSection, dashboardTranslations));

        return this.reorderExtraBalanceToEnd(subItemsViewModel);
    }

    /** 🔧 Helper: Build common fixe subitem with tracking and link */
    private static buildFixeSubItem(
        cid: string,
        label: string,
        picto: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.createBasePictoSubItem(cid, label, picto);
        const link = DashboardV7MapperUtils.getConsoPathFixe(cid, itemSection.equipmentId, dashboardTranslations);
        return this.addConsoTracking(subItem, link);
    }

    private static buildFixeErrorSubItem(
        bundle: Bundle,
        cid: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.buildFixeSubItem(
            cid,
            dashboardTranslations['deviceHome']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            itemSection,
            dashboardTranslations,
        );
        subItem.consumption = dashboardTranslations['deviceHome']['error'];
        return subItem;
    }

    private static buildFixeCreditSubItem(
        bundle: Bundle,
        summary: BundleSummary,
        cid: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.buildFixeSubItem(
            cid,
            dashboardTranslations['deviceHome']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            itemSection,
            dashboardTranslations,
        );

        subItem.consumption = dashboardTranslations['deviceHome']['restTextConsumption'].concat(
            summary.mainCredit
                ? DashboardV7MapperUtils.convertMainCredit(summary.mainCredit.value, summary.mainCredit.unit)
                : ' €',
        );

        if (bundle.bundleCode === BundleCodeEnum.EPUISE) {
            subItem.consumption = dashboardTranslations['deviceHome']['exhaustedText'];
        }

        return subItem;
    }

    private static buildFixeCommunicationsSubItem(
        bundle: Bundle,
        summary: BundleSummary,
        cid: string,
        itemSection: ItemSection,
        dashboardTranslations: any,
    ): SubItemViewModel {
        const subItem = this.buildFixeSubItem(
            cid,
            dashboardTranslations['deviceHome']['consumption'],
            dashboardTranslations['picto']['GAUGE_RESEAU'],
            itemSection,
            dashboardTranslations,
        );

        const mainCreditValue = bundle.summary.mainCredit.value;

        if (mainCreditValue >= 0) {
            subItem.consumption = dashboardTranslations['deviceHome']['cosnumptionText'].concat(
                DashboardV7MapperUtils.convertMainCredit(summary.mainCredit.value, summary.mainCredit.unit) + ' €',
            );
        } else if (bundle.bundleCode === BundleCodeEnum.EPUISE) {
            subItem.consumption = dashboardTranslations['deviceHome']['exhaustedText'];
        }

        return subItem;
    }

    public static extractSubItemFixe(
        itemSection: ItemSection,
        cid: string,
        dashboardTranslations: any,
    ): SubItemViewModel[] | null {
        const subItemsViewModel: SubItemViewModel[] = [];

        if (!Object.hasOwn(itemSection, 'consoFamilies')) {
            return subItemsViewModel;
        }

        for (const consoFamily of itemSection.consoFamilies) {
            if (!Object.hasOwn(consoFamily, 'bundle')) {
                continue;
            }

            const bundle: Bundle = consoFamily.bundle;
            const summary: BundleSummary = bundle.summary;
            let subItem: SubItemViewModel | null = null;

            if (consoFamily.familyCode === ConsoFamilyEnum.error) {
                subItem = this.buildFixeErrorSubItem(bundle, cid, itemSection, dashboardTranslations);
            } else if (consoFamily.familyCode === ConsoFamilyEnum.credit) {
                subItem = this.buildFixeCreditSubItem(bundle, summary, cid, itemSection, dashboardTranslations);
            } else if (consoFamily.familyCode === ConsoFamilyEnum.communicationsPayantes) {
                subItem = this.buildFixeCommunicationsSubItem(bundle, summary, cid, itemSection, dashboardTranslations);
            }

            if (subItem) {
                subItemsViewModel.push(subItem);
            }
        }

        return subItemsViewModel;
    }

    private static getEquipment(equipment: Equipment, translation: any): SubItemViewModel {
        const subItem: SubItemViewModel = new SubItemViewModel();
        subItem.type = SubItemTypeEnum.picto;
        subItem.trackingArea = TmsCodes.care_hp_mobile;
        subItem.cid = equipment.cid;
        subItem.picto = translation['picto'][equipment.imageId];
        subItem.label = equipment.label;
        subItem.trackingName = equipment.label;
        subItem.detail = [];
        subItem.detailLong = [];
        if (Object.hasOwn(equipment, 'detail')) {
            subItem.detail.push(equipment.detail);
            subItem.detailLong.push(equipment.detail);
        }
        if (equipment.cid && equipment.usageCode) {
            subItem.link = DashboardV7MapperUtils.getEquipmentPath(equipment.usageCode, equipment.usageType);
        }
        return subItem;
    }

    private static getWhoUsesIt(itemSection: ItemSection, translation: any): SubItemViewModel {
        return Object.assign(new SubItemViewModel(), {
            label: itemSection.whoUsesIt.label,
            cid: itemSection.cid,
            type: SubItemTypeEnum.picto,
            trackingArea: TmsCodes.care_hp_mobile,
            trackingName: itemSection.whoUsesIt.label,
            picto: translation['picto']['USER_UNKNOW'],
            link: Object.assign(new Link(), translation['whoUsesIt']['link']),
            linkParam: translation['whoUsesIt']['linkParam'],
        });
    }

    private static getService24(itemSection: ItemSection, translation: any): SubItemViewModel {
        return Object.assign(new SubItemViewModel(), {
            cid: itemSection.cid,
            type: SubItemTypeEnum.picto,
            picto: translation['picto']['SERVICE_24'],
            label: itemSection.service24.title,
            detailLong: [itemSection.service24.text],
            link: itemSection.service24.link,
            trackingArea: TmsCodes.care_hp_mobile,
            trackingName: TrackedName.SERVICE_24H,
        });
    }

    public static extractImage(itemSection: ItemSection): string {
        let image: string = DashboardV7MapperUtils.defaultMobileImage;
        if (itemSection.title?.imageUrl) {
            image = itemSection.title.imageUrl;
        } else if (itemSection.usageCode) {
            switch (itemSection.usageCode) {
                case UsageCodeEnum.CODE41001:
                    image = DashboardV7MapperUtils.defaultTabletImage;
                    break;
                case UsageCodeEnum.CODE43001:
                    image = DashboardV7MapperUtils.defaultMultisimImage;
                    break;
                case UsageCodeEnum.CODE44001:
                    image = DashboardV7MapperUtils.defaultMultisimImage;
                    break;
                case UsageCodeEnum.CODE45001:
                    image = DashboardV7MapperUtils.airboxImage;
                    break;
                default:
                    break;
            }
        }
        return image;
    }
}
